import React from "react";
import { Route, Routes } from "react-router-dom";

import HostScreen from '@modules/devices/HostScreen';
import NotFound from '@components/not-found/not-found';
import RequirePlanModule from '@components/router/require-plan-module';
import { PlanModule } from '@interfaces/permission_access';

const LandingPageComponent = React.lazy(
    () => import("@modules/practitioner/landing-page/landing-page")
);
const ProfileComponent = React.lazy(
    () => import("@modules/practitioner/profile/practitioner-profile")
);
const PrivateProfileComponent = React.lazy(
    () =>
        import("@modules/practitioner/profile/private-profile/private-profile")
);
const PublicProfileComponent = React.lazy(
    () => import("@modules/practitioner/profile/public/PublicProfile")
);
const PublicProfileComponentView = React.lazy(
    () => import("@modules/practitioner/profile/practitioner-public-profile")
);
const ModalLayout = React.lazy(
    () => import("@modules/practitioner/profile/modal-layout")
);
const PublicPreview = React.lazy(
    () => import("@modules/practitioner/profile/public-preview")
);
const CalendarComponent = React.lazy(
    () => import("@modules/practitioner/calendar/calendar")
);

const AppointmentsComponent = React.lazy(
    () => import("@modules/practitioner/appointments/appointments")
);

const EpocQuizTable = React.lazy(
    () => import("@modules/practitioner/patients/questionnaire/epoc-quiz-table")
);
const EpocQuiz = React.lazy(
    () => import("@modules/practitioner/patients/questionnaire/epoc-quiz")
);
const PatientsComponent = React.lazy(
    () => import("@modules/practitioner/patients/patients")
);
const PatientComponent = React.lazy(
    () => import("@modules/practitioner/patients/patient")
);
const PatientDetailsComponent = React.lazy(
    () =>
        import("@modules/practitioner/patients/patient-details/patient-details")
);
const PatienteReferencesComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/patient-details/report/References"
        )
);
const PatienteReferencesRecipe = React.lazy(
    () => import("@modules/practitioner/patients/patient-details/recipe/Recipe")
);
const PatientReferencesMedical = React.lazy(
    () =>
        import("@modules/practitioner/patients/patient-details/medical/Medical")
);
const InformedConsentLetterComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/patient-details/medical/informed-consent-letter"
        )
);
const PatientHealthDataComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/patient-health-data/patient-healthdata"
        )
);
const DiagnosisFormComponent = React.lazy(
    () => import("@modules/practitioner/patients/diagnosis/new-diagnosis")
);
const TreatmentFormComponent = React.lazy(
    () => import("@modules/practitioner/patients/treatments/new-treatment")
);
const WorkplanFormComponent = React.lazy(
    () => import("@modules/practitioner/patients/workplan/new-workplan")
);
const PatientDocumentsComponent = React.lazy(
    () => import("@modules/practitioner/patients/documents/documents")
);
const TransferDocumentsComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/documents/tranfer-documents/trasnfer-docs"
        )
);
const PatientStudiesComponent = React.lazy(
    () => import("@modules/practitioner/patients/studies/studies-card")
);
const NewStudyComponent = React.lazy(
    () => import("@modules/practitioner/patients/studies/new-study")
);
const PatientConsultComponent = React.lazy(
    () =>
        import("@modules/practitioner/patients/consult-deatils/consult-details")
);
const NewAppointmentComponent = React.lazy(
    () =>
        import("@modules/practitioner/patients/consult-deatils/new-appointment")
);
const RecordSummaryComponent = React.lazy(
    () => import("@modules/practitioner/patients/new-record/record-summary")
);
const OfficesPanelComponent = React.lazy(
    () => import("@modules/practitioner/offices/offices-panel")
);
const OfficesCreateComponent = React.lazy(
    () => import("@modules/practitioner/offices/create/offices-create")
);
const ServicesComponent = React.lazy(
    () => import("@modules/practitioner/services/services")
);
const ServicesViewComponent = React.lazy(
    () => import("@modules/practitioner/services/services-view")
);
const ConfigServiceStep = React.lazy(
    () =>
        import(
            "@modules/practitioner/services/config-service/config-service-step-container"
        )
);
// const NewRecordComponent = React.lazy(
//     () => import("@modules/practitioner/patients/new-record/new-record")
// );
const NewRecordWrapper = React.lazy(
    () => import("@modules/practitioner/patients/consult-deatils/new-record-wrapper")
);
const PatientHealthDataForm = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/patient-health-data/patient-health-form"
        )
);
const CreateAppointmentComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/schedule-appointment/schedule-appointment"
        )
);
const NewPatientComponent = React.lazy(
    () => import("@modules/practitioner/patients/new-patient/new-patient")
);
const OrganizationComponent = React.lazy(
    () => import("@modules/practitioner/organization/organization")
);
const BlogComponent = React.lazy(
    () => import("@modules/practitioner/blog/blog")
);
const ChatComponent = React.lazy(
    () => import("@modules/practitioner/chat/chat")
);
const PatientMedicalQuizComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patient-medical-record-background/patient-medical-quiz"
        )
);
const PatientMedicalFamilyQuizComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patient-medical-record-background/patient-medical-family-quiz"
        )
);
const AddServicesComponent = React.lazy(
    () => import("@modules/practitioner/services/addService/addservices")
);
const AddOfficesComponent = React.lazy(
    () => import("@modules/practitioner/services/addOffice/addoffices")
);
const RelateServicesComponent = React.lazy(
    () => import("@modules/practitioner/services/relateservices")
);
const PatientFilePreviewComponent = React.lazy(
    () =>
        import("@modules/practitioner/patient-appointment/patient-file-preview")
);
const PatientAppointmentComponent = React.lazy(
    () =>
        import("@modules/practitioner/patient-appointment/patient-appointment")
);
const PatientFileComponent = React.lazy(
    () => import("@modules/practitioner/patient-file/patient-file")
);
const PatientMedicalRecordComponent = React.lazy(
    () => import("@modules/practitioner/patient-record/patient-medical-record")
);
const PatientConsentLetterRecordComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patient-record/patient-informed-consent-letter"
        )
);
const PatientMedicalRecordBackgroundComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patient-medical-record-background/patient-medical-record-background-template"
        )
);
const PatientReferenceRecordComponent = React.lazy(
    () =>
        import("@modules/practitioner/patient-record/patient-reference-record")
);
const PatientRecipeRecordComponent = React.lazy(
    () => import("@modules/practitioner/patient-record/patient-recipe-record")
);
const AddServiceComponent = React.lazy(
    () => import("@modules/practitioner/services/addService/addservices")
);
const ComponentList = React.lazy(() => import("@components/render-component"));
const CardLobbyTeleconsult = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/card-lobby-teleconsult/card-lobby-teleconsult"
        )
);
const PrognosisDetail = React.lazy(
    () => import("@modules/practitioner/patients/prognosis/prognosis-detail")
);
const GroupVideoCallContainer = React.lazy(
    () => import("@modules/practitioner/GroupVideoCall/GroupVideoCallContainer")
);
const CasesData = React.lazy(
    () => import("@modules/practitioner/patients/cases/casesData")
);
const QuizzesNomComponent = React.lazy(
    () => import("@modules/practitioner/quizzes-nom/quizzes-nom")
);
const QuizWrapper = React.lazy(
    () => import("@modules/practitioner/patients/new-record/quiz/quiz-wrapper")
);
// const QuizzesNomComponent2 = React.lazy(() => import('@modules/practitioner/quiz-nom/quiz-nom'));
const AddQuizNomComponent = React.lazy(
    () => import("@modules/practitioner/quizzes-nom/add-quiz-nom/add-quiz-nom")
);
const QuizzesNomTable = React.lazy(
    () => import("@modules/practitioner/quizzes-nom/quzzes-table/quizzes-table")
);
const DetailQuizNomComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/quizzes-nom/detail-quiz-nom/detail-quiz-nom"
        )
);
const IndividualReportQuiz = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/questionnaire/new-individual-report/individual-report-nomt"
        )
);
const GeneralReportQuiz = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/questionnaire/new-general-report/general-report-nom"
        )
);
const QuizzesNomResults = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/patient-details/quizzes-nom-results/quizzes-nom-results"
        )
);
const QuizzesNomResultDetail = React.lazy(
    () => import("@modules/practitioner/patients/questionnaire/quizz-nom-table")
);
const QuizzesEpocResults = React.lazy(
    () => import("@modules/practitioner/patients/questionnaire/epoc-quiz")
);

const ParticipantView = React.lazy(
    () =>
        import(
            "@modules/practitioner/quizzes-nom/participant-view/participant-list"
        )
);
const StudyFromDevicesComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/new-record/study-from-devices/study-from-devices"
        )
);
const PatientPersonalDataForm = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/patient-details/summary/patient-personal-data-form/PatientPersonalDataForm"
        )
);
const PatientReferencesStudy = React.lazy(
    () => import("@modules/practitioner/patients/patient-details/study/study")
);
const PatientReferencesStudyRecord = React.lazy(
    () => import("@modules/practitioner/patient-record/patient-study-record")
);
const PatientNonPathologicForm = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/patient-details/summary/patient-personal-data-form/patient-non-pathologic-form"
        )
);
const PatientUploadDocument = React.lazy(
    () =>
        import("@modules/practitioner/patients/documents/PatientUploadDocument")
);
const WorplanActivityListContainer = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/workplan/activity-list/activity-list-container"
        )
);
const WorkplanActivityFormComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/workplan/components/new-workplan-activity"
        )
);
const WorkplanActivityDetail = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/workplan/components/workplan-activity-detail"
        )
);
const QuizResultDetail = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/patient-health-data/quiz/quiz-result-detail"
        )
);
const QuizDetailResultById = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/patient-health-data/quiz/quiz-detail-result-by-id"
        )
);
const ConsultRecordDetailsComponent = React.lazy(
    () =>
        import(
            "@modules/practitioner/patients/consult-record-details/consult-record-details"
        )
);
const AddDisabilities = React.lazy(
    () => import("@modules/practitioner/patients/disabilities/add-disabilities")
);
const PractitionerAddSchedulesComponent = React.lazy(
    () => import("@modules/practitioner/services/addSchedule/addSchedules")
);
const PaySubscriptionPage = React.lazy(
    () =>
        import(
            "@modules/practitioner/profile/pay-subscription/pay-subscription-page"
        )
);

const PractitionerRoutes = () => {
    return (
        <Routes>
            <Route
                path="/epoc-quiz-table"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><EpocQuizTable /></RequirePlanModule>}
                key="practitioner-epoc-quiz-table"
            />
            <Route path="/epoc-quiz" element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><EpocQuiz /></RequirePlanModule>} />
            <Route
                path="/dashboard"
                element={<LandingPageComponent />}
            />
            <Route
                path="/profile-private"
                element={<ProfileComponent />}
            />
            <Route
                path="/profile"
                element={<PrivateProfileComponent />}
            />
            <Route
                path="/public-profile"
                element={<PublicProfileComponent />}
            />
            <Route
                path="/public-profile-view"
                element={<PublicProfileComponentView />}
            />
            <Route
                path="/modal-layout"
                element={<ModalLayout />}
            />
            <Route
                path="/public-preview"
                element={<PublicPreview />}
            />
            <Route
                path="/addservices"
                element={<RequirePlanModule module={PlanModule.SERVICES}><AddServicesComponent /></RequirePlanModule>}
            />
            <Route
                path="/relateservices"
                element={<RequirePlanModule module={PlanModule.SERVICES}><RelateServicesComponent /></RequirePlanModule>}
            />
            <Route
                path="/addoffices"
                element={<RequirePlanModule module={PlanModule.OFFICES}><AddOfficesComponent /></RequirePlanModule>}
            />
            <Route
                path="/offices"
                element={<OfficesPanelComponent />}
            />
            <Route
                path="/offices-create"
                element={<RequirePlanModule module={PlanModule.OFFICES}><OfficesCreateComponent /></RequirePlanModule>}
            />
            <Route
                path="/services/config-schedules"
                element={<RequirePlanModule module={PlanModule.SERVICES}><ConfigServiceStep /></RequirePlanModule>}
            />
            <Route
                path="/services/config-service"
                element={<RequirePlanModule module={PlanModule.SERVICES}><ConfigServiceStep /></RequirePlanModule>}
            />
            <Route
                path="/services-before"
                element={<ServicesComponent />}
            />
            <Route
                path="/services"
                element={<ServicesViewComponent />}
            />
            <Route
                path="/calendar"
                element={<CalendarComponent />}
            />
            <Route path="/devices" element={<HostScreen />} />
            <Route
                path="/patient-full"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientsComponent /></RequirePlanModule>}
            />
            <Route
                path="/patients"
                element={<PatientComponent />}
            />
            <Route
                path="/patient/reference"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatienteReferencesComponent /></RequirePlanModule>}
            />
            <Route
                path="/patient/recipe"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatienteReferencesRecipe /></RequirePlanModule>}
            />
            <Route
                path="/patient/study"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientReferencesStudy /></RequirePlanModule>}
            />
            <Route
                path="/patient/medical"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientReferencesMedical /></RequirePlanModule>}
            />
            <Route
                path="/patient/informed-consent-letter"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><InformedConsentLetterComponent /></RequirePlanModule>}
            />
            <Route
                path="/create-appointment"
                element={<CreateAppointmentComponent />}
            />
            <Route
                path="/patient"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientDetailsComponent /></RequirePlanModule>}
            />
            <Route
                path="/studies/new-study"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><NewStudyComponent /></RequirePlanModule>}
            />
            <Route
                path="/new-patient"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><NewPatientComponent /></RequirePlanModule>}
            />
            <Route
                path="/new-record"
                // element={<NewRecordComponent />}
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><NewRecordWrapper /></RequirePlanModule>}
            />
            <Route
                path="/new-appointment"
                element={<NewAppointmentComponent />}
            />
            <Route
                path="/summary"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><RecordSummaryComponent /></RequirePlanModule>}
            />
            <Route
                path="/new-diagnosis"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><DiagnosisFormComponent /></RequirePlanModule>}
            />
            <Route
                path="/new-treatment"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><TreatmentFormComponent /></RequirePlanModule>}
            />
            <Route
                path="/new-workplan"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><WorkplanFormComponent /></RequirePlanModule>}
            />
            <Route
                path="/patient-health"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientHealthDataComponent /></RequirePlanModule>}
            />
            <Route
                path="/patient-documents"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientDocumentsComponent /></RequirePlanModule>}
            />
            <Route
                path="/transfer-docs"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><TransferDocumentsComponent /></RequirePlanModule>}
            />
            <Route
                path="/patient-studies"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientStudiesComponent /></RequirePlanModule>}
            />
            <Route
                path="/patient-consult"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientConsultComponent /></RequirePlanModule>}
            />
            <Route
                path="/appointments"
                element={<AppointmentsComponent />}
            />
            <Route
                path="/patient/appointment-file-preview"
                element={<PatientFilePreviewComponent />}
            />
            <Route
                path="/patient/appointment"
                element={<PatientAppointmentComponent />}
            />
            <Route
                path="/patient/prev-appointment"
                element={<PatientAppointmentComponent />}
            />
            <Route
                path="/medical-record"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientMedicalRecordComponent /></RequirePlanModule>}
            />
            <Route
                path="/consent-letter-record"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientConsentLetterRecordComponent /></RequirePlanModule>}
            />
            <Route
                path="/medical-record-background"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientMedicalRecordBackgroundComponent /></RequirePlanModule>}
            />
            <Route
                path="/reference-record"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientReferenceRecordComponent /></RequirePlanModule>}
            />
            <Route
                path="/recipe-record"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientRecipeRecordComponent /></RequirePlanModule>}
            />
            <Route
                path="/study-record"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientReferencesStudyRecord /></RequirePlanModule>}
            />
            <Route
                path="/patient/file"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientFileComponent /></RequirePlanModule>}
            />
            <Route
                path="/add-service"
                element={<RequirePlanModule module={PlanModule.SERVICES}><AddServiceComponent /></RequirePlanModule>}
            />
            <Route
                path="/office"
                element={<RequirePlanModule module={PlanModule.OFFICES}><AddOfficesComponent /></RequirePlanModule>}
            />
            <Route
                path="/patient-form"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientHealthDataForm /></RequirePlanModule>}
            />
            <Route
                path="/components"
                element={<ComponentList />}
            />
            <Route
                path="/organization"
                element={<OrganizationComponent />}
            />
            <Route path="/chat" element={<ChatComponent />} />
            <Route path="/blog" element={<BlogComponent />} />
            <Route
                path="/temporal-quiz"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientMedicalQuizComponent /></RequirePlanModule>}
            />
            <Route
                path="/temporal-quiz-family"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientMedicalFamilyQuizComponent /></RequirePlanModule>}
            />
            <Route
                path="/lobby-teleconsult"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><CardLobbyTeleconsult /></RequirePlanModule>}
            />
            <Route
                path="/prognosis-detail"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PrognosisDetail /></RequirePlanModule>}
            />
            <Route
                path="/group-videocall"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><GroupVideoCallContainer /></RequirePlanModule>}
            />
            <Route path="/case-info" element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><CasesData /></RequirePlanModule>} />
            <Route
                path="/quiz"
                element={<QuizzesNomComponent />}
            />
            <Route
                path="/quiz-patient/:quizName/patient/:patientUsername"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><QuizWrapper /></RequirePlanModule>}
            />
            <Route
                path="/quiz-nom-list"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><QuizzesNomTable /></RequirePlanModule>}
            />
            <Route
                path="/add-quiz-nom"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><AddQuizNomComponent /></RequirePlanModule>}
            />
            <Route
                path="/quiz-nom-detail"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><DetailQuizNomComponent /></RequirePlanModule>}
            />
            <Route
                path="/quiz-nom-individual-report"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><IndividualReportQuiz /></RequirePlanModule>}
            />
            <Route
                path="/quiz-nom-general-report"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><GeneralReportQuiz /></RequirePlanModule>}
            />
            <Route
                path="/quiz-nom-results"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><QuizzesNomResults /></RequirePlanModule>}
            />
            <Route
                path="/quiz-epoc-results"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><QuizzesEpocResults /></RequirePlanModule>}
            />
            <Route
                path="/quiz-nom-result-detail"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><QuizzesNomResultDetail /></RequirePlanModule>}
            />
            <Route
                path="/quizzes/participant"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><ParticipantView /></RequirePlanModule>}
            />
            <Route
                path="/capture-from-device"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><StudyFromDevicesComponent /></RequirePlanModule>}
            />
            <Route
                path="/patient-personal-data-form"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientPersonalDataForm /></RequirePlanModule>}
            />
            <Route
                path="/patient-non-pathological-form"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientNonPathologicForm /></RequirePlanModule>}
            />
            <Route
                path="/patient-upload-document"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><PatientUploadDocument /></RequirePlanModule>}
            />
            <Route
                path="/add-schedules"
                element={<PractitionerAddSchedulesComponent />}
            />
            <Route
                path="/workplan-activity-list"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><WorplanActivityListContainer /></RequirePlanModule>}
            />
            <Route
                path="/new-workplan-activity"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><WorkplanActivityFormComponent /></RequirePlanModule>}
            />
            <Route
                path="/workplan-activity-detail"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><WorkplanActivityDetail /></RequirePlanModule>}
            />
            <Route
                path="/quiz-detail-result/:quizName"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><QuizResultDetail /></RequirePlanModule>}
            />
            <Route
                path="/consult-record-details"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><ConsultRecordDetailsComponent /></RequirePlanModule>}
            />
            <Route
                path="/quiz-detail-result-by-id/:quizzId"
                element={<RequirePlanModule module={PlanModule.TELEMEDICINE}><QuizDetailResultById /></RequirePlanModule>}
            />
            <Route
                path="/add-disabilities"
                element={<RequirePlanModule module={PlanModule.CLINIC_HISTORY}><AddDisabilities /></RequirePlanModule>}
            />
            <Route
                path="/pay-subscription/:subscriptionType"
                element={<PaySubscriptionPage />}
            />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
};

export default PractitionerRoutes;
